import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Scale, Zap, Receipt, Bell, ShieldCheck, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const FEATURES = [
  { icon: Zap, title: "Real-time sync", desc: "Expenses & balances update instantly for every roommate." },
  { icon: Receipt, title: "Receipts", desc: "Snap a photo, shared with the whole house." },
  { icon: Scale, title: "Fair splits", desc: "Automatic equal shares and who-owes-who." },
  { icon: Bell, title: "Live alerts", desc: "Know the moment anything changes." },
];

export default function Login() {
  const { user, household, login, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate(household ? "/dashboard" : "/onboarding", { replace: true });
  }, [user, household, loading, navigate]);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl" />
      <div className="absolute -bottom-32 -left-20 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl" />

      <div className="relative max-w-md mx-auto px-6 pt-16 pb-10 flex flex-col min-h-screen">
        <div className="flex items-center gap-3 mb-10 animate-rise">
          <div className="h-12 w-12 rounded-2xl bg-primary flex items-center justify-center text-primary-foreground font-heading font-extrabold text-2xl">R</div>
          <div className="font-heading font-extrabold text-2xl tracking-tight">RoomieSplit</div>
        </div>

        <div className="animate-rise" style={{ animationDelay: "60ms" }}>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
            Shared living,<br />
            <span className="text-primary">simply settled.</span>
          </h1>
          <p className="mt-4 text-muted-foreground text-base leading-relaxed">
            Split rent, utilities and groceries with your roommates. See everyone's balance update live and settle up via Zelle each month.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-3 animate-rise" style={{ animationDelay: "120ms" }}>
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-2xl border border-border bg-card p-4">
              <f.icon className="h-5 w-5 text-primary mb-2" />
              <div className="font-semibold text-sm">{f.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5 leading-snug">{f.desc}</div>
            </div>
          ))}
        </div>

        <div className="mt-auto pt-10 animate-rise" style={{ animationDelay: "180ms" }}>
          <Button onClick={login} size="lg" data-testid="google-signin-btn" className="w-full h-13 py-6 rounded-2xl text-base font-semibold shadow-lg shadow-primary/20">
            <GoogleIcon /> Continue with Google
            <ArrowRight className="h-4 w-4 ml-auto" />
          </Button>
          <p className="text-center text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" /> Secure Google sign-in · your data stays private
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-5 w-5 mr-1" viewBox="0 0 24 24">
      <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.5l-6.6-5.6C29.6 34.6 26.9 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5h-1.9V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.6 5.6C41.6 36 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z" />
    </svg>
  );
}
